/**
 * TOSS Web Application — требования к паролю, в одном месте на весь продукт
 *
 * Раньше эти пять правил жили двумя наборами: клиентский `clientPasswordPolicy.ts` (правила с
 * предикатами, из которых собирается и схема, и живой чек-лист) и внутренний
 * `ResetPasswordPage` (та же пятёрка, но переписанная руками — регулярки в zod плюс серый
 * список в рамке, который читают уже ПОСЛЕ отказа). Наборы совпадали по смыслу и расходились
 * по формулировкам, а расходились бы дальше — их правили в разных файлах.
 *
 * **Правила строже, чем требует бэкенд, и это намеренно.** `AuthService.ChangePasswordAsync`
 * делегирует ASP.NET Identity с `RequireNonAlphanumeric = false`, а `AuthService.IsPasswordValid`
 * (сброс пароля) спецсимвол требует. Взята строгая сторона: пароль, прошедший её, сервер примет
 * в любом случае — обратное неверно.
 *
 * Каждое правило несёт свой `test`, и это то, что делает возможным живой чек-лист: один и тот
 * же предикат зажигает галочку и строит проверку zod, поэтому список не может сказать «готово»
 * о том, что схема через мгновение отвергнет.
 */

import { z } from 'zod';

// ============================================================================
// Rules
// ============================================================================

export interface PasswordRule {
  /** Строка чек-листа — короткая, её читают на ходу. */
  label: string;
  /** Сообщение, которым zod отвечает, когда не выполнено только это правило. */
  message: string;
  test: (value: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    label: 'Минимум 6 символов',
    message: 'Пароль должен содержать минимум 6 символов',
    test: (value) => value.length >= 6,
  },
  {
    label: 'Заглавная буква',
    message: 'Пароль должен содержать заглавную букву',
    test: (value) => /[A-Z]/.test(value),
  },
  {
    label: 'Строчная буква',
    message: 'Пароль должен содержать строчную букву',
    test: (value) => /[a-z]/.test(value),
  },
  {
    label: 'Цифра',
    message: 'Пароль должен содержать цифру',
    test: (value) => /[0-9]/.test(value),
  },
  {
    label: 'Спецсимвол (!@#$%^&*())',
    message: 'Пароль должен содержать спецсимвол (!@#$%^&*())',
    test: (value) => /[!@#$%^&*()]/.test(value),
  },
];

// ============================================================================
// Schema
// ============================================================================

/**
 * Правила применяются по порядку, поэтому сообщение говорит о ПЕРВОМ недостающем, а не о
 * последнем проверенном.
 */
export const newPasswordSchema = PASSWORD_RULES.reduce(
  (schema, rule) => schema.refine(rule.test, { message: rule.message }),
  z.string() as z.ZodType<string>
);
